
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

declare const __APP_VERSION__: string;
const CURRENT_VERSION: string = __APP_VERSION__;
const DISMISSED_KEY = 'version_check_dismissed';

export function VersionCheckDialog() {
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    checkVersion();
  }, []);

  async function checkVersion() {
    try {
      // Check if user already dismissed this session
      const dismissed = sessionStorage.getItem(DISMISSED_KEY);
      if (dismissed === CURRENT_VERSION) return;

      const { data, error } = await (supabase as any)
        .from('app_versions')
        .select('version_code')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error || !data) return;

      // Only show update dialog if the DB version is NEWER than current
      if (data.version_code !== CURRENT_VERSION && data.version_code > CURRENT_VERSION) {
        setLatestVersion(data.version_code);
        setOpen(true);
      }
    } catch {
      // Silently fail
    }
  }

  function handleUpdate() {
    if ('caches' in window) {
      caches.keys().then((names: string[]) => {
        return Promise.all(names.map((name: string) => caches.delete(name)));
      }).then(() => {
        location.reload();
      });
    } else {
      location.reload();
    }
  }

  function handleContinue() {
    try {
      sessionStorage.setItem(DISMISSED_KEY, CURRENT_VERSION);
    } catch {}
    setOpen(false);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-sm rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-lg">
            <RefreshCw size={20} className="text-primary" />
            Update Available
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            A new version is available. Please update for the best experience.
            <br />
            <span className="text-xs text-muted-foreground mt-1 block">
              Current: {CURRENT_VERSION} → Latest: {latestVersion}
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-row gap-2">
          <Button variant="outline" onClick={handleContinue} className="flex-1">
            Continue
          </Button>
          <Button onClick={handleUpdate} className="flex-1">
            Update
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
